// hooks/useModalForm.js
import { Form } from "antd";
import { useState } from "react";
import { parseDefaultValue } from "../helpers/formFieldUtils";

// 🔑 persistent cache across calls
const optionsCache = new Map();

export function useModalForm(modalConfigs, dependencies = {}) {
  const [appform] = Form.useForm();
  const [state, setState] = useState({
    open: false,
    type: null,
    mode: "add",
    fields: [],
    title: "Modal",
  });

  const openModal = async (
    type,
    mode = "add",
    title = "Modal",
    defaultValues = {}
  ) => {
    let fields = modalConfigs[type].map((field) => ({
      ...field,
      defaultValue: parseDefaultValue(field, defaultValues[field.name]),
    }));

    // Resolve dynamic options with caching
    const promises = fields.map(async (field) => {
      if (field.fetchOptions) {
        const key = field.fetchKey || field.name; // fallback to field name

        if (optionsCache.has(key)) {
          return { ...field, options: optionsCache.get(key) };
        }

        try {
          const res = await field.fetchOptions();
          optionsCache.set(key, res || []);
          return { ...field, options: res || [] };
        } catch (err) {
          console.error("Error fetching options for", field.name, err);
          return { ...field, options: [] };
        }
      }
      return field;
    });

    fields = await Promise.all(promises);

    if (mode === "add") {
      appform.resetFields();
    } else {
      const normalizedValues = {};
      modalConfigs[type].forEach((field) => {
        normalizedValues[field.name] = parseDefaultValue(
          field,
          defaultValues[field.name]
        );
      });
      appform.setFieldsValue(normalizedValues);
    }

    setState({ open: true, type, mode, title, fields });
  };

  const closeModal = () => {
    appform.resetFields();
    setState((prev) => ({ ...prev, open: false }));
  };

  const handleValuesChange = (changedValues, allValues) => {
    Object.keys(changedValues).forEach((changedKey) => {
      if (dependencies[changedKey]) {
        const updater = dependencies[changedKey];
        const updates = updater(changedValues[changedKey], allValues);
        appform.setFieldsValue(updates);
      }
    });
  };

  return {
    appform,
    state,
    openModal,
    closeModal,
    handleValuesChange,
  };
}
